import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAuthStore,
  transformApiUser,
  type ApiUserProfile,
} from '@teletienda/shared-auth-ui';

import { coreAPI_Service } from '@/lib/api/core';
import type { User } from '@/types';

/**
 * UPROF-1.7 — apply a fresh profile to BOTH client copies of user state, from a
 * single object, without a refetch and without flipping the auth store's global
 * `isLoading`.
 *
 * Why this replaces the 1.2 `useProfileSync` → `fetchProfile()` flow:
 *  - `fetchProfile()` toggles the auth store `isLoading`, which `AuthGuard` treats
 *    as "unmount the page and show the loader" — so every save remounted `/profile`
 *    (the reported regression). This helper never touches that flag.
 *  - The save fanned out to TWO racing `GET /me` calls (`invalidateQueries` +
 *    `fetchProfile`); the remounted Mantine form then seeded from whichever lost
 *    the race. Here a single object updates both copies — no race.
 *
 * ⚠ RBAC asymmetry (verified against the live API): `GET /users/me` enriches the
 * body with `roles`/`permissions` (ProfileController loads them from Redis/DB),
 * but `PUT /users/me` returns only the profile entity (`toJSON()`, no RBAC). So we
 * must NOT blindly replace the auth-store user with `transformApiUser(PUT body)` —
 * that would wipe roles and break `AuthGuard`. `applyProfile` therefore PRESERVES
 * RBAC/merchant context that the given body omits, and merges (not replaces) the
 * React Query cache so partial bodies never drop fields (completion %, kyc, etc.).
 */
export function useApplyProfile() {
  const queryClient = useQueryClient();

  /** Update both the `['userProfile']` query cache and the auth store from one profile object. */
  const applyProfile = useCallback(
    (profile: User) => {
      // 1) React Query cache that ProfilePage reads — merge so a partial body
      //    (e.g. a PUT response) never clobbers fields it didn't return.
      queryClient.setQueryData<User>(['userProfile'], (old) =>
        old ? { ...old, ...profile } : profile,
      );

      // 2) Auth store (the source the header reads). Derive name/avatarUrl via the
      //    canonical mapper, but preserve RBAC/merchant/identity fields the body omits.
      const mapped = transformApiUser(profile as unknown as ApiUserProfile);
      const current = useAuthStore.getState().user;
      useAuthStore.getState().updateUser({
        ...current,
        ...mapped,
        userGuid: mapped.userGuid ?? current?.userGuid,
        email: mapped.email ?? current?.email,
        roles: mapped.roles.length ? mapped.roles : current?.roles ?? [],
        permissions: mapped.permissions.length ? mapped.permissions : current?.permissions ?? [],
        merchantGuid: mapped.merchantGuid ?? current?.merchantGuid,
        merchantGuids: mapped.merchantGuids ?? current?.merchantGuids,
      });
    },
    [queryClient],
  );

  /**
   * For paths that carry no profile in hand (WS `PROFILE_UPDATED` ping, avatar
   * change, cross-tab currency sync): one silent `GET /users/me` (which DOES include
   * RBAC), then apply. No `fetchProfile()`, so no `isLoading` flip and no remount.
   */
  const refreshProfile = useCallback(async () => {
    const fresh = await coreAPI_Service.getMyProfile();
    applyProfile(fresh);
  }, [applyProfile]);

  return { applyProfile, refreshProfile };
}
